import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import AuthProvider from "./providers/auth.provider";
import LoginPage from "../login/login.page";
import GeneralError from "../../shared/errors/general-error";
import MaintenanceError from "../../shared/errors/maintenance-error";
import NotFoundError from "../../shared/errors/not-found-error";
import UnauthorizedError from "../../shared/errors/unauthorized-error";
import ProtectedLayout from "./protected-routes/route";

// Pre-define lazy components
const Landing = lazy(() => import("../landing/landing"));
const SignupPage = lazy(() => import("../login/signup.page"));
const VerifyPage = lazy(() => import("../login/verify.page"));
const OAuthPage = lazy(() => import("../login/oauth.page"));
const PrivacyPolicyPage = lazy(() => import("../legal/privacy.page"));
const TermsOfServicePage = lazy(() => import("../legal/terms.page"));
const CookiePolicyPage = lazy(() => import("../legal/cookie.page"));
const ForgotPasswordPage = lazy(() => import("../login/forgot-password.page"));
const StaffAccessPage = lazy(() => import("../login/staff-access.page"));
const SettingsPage = lazy(() => import("../admin/settings/settings.page"));

const router = createBrowserRouter([
  {
    element: <AuthProvider />,
    children: [
      {
        path: "/",
        Component: Landing,
      },
      {
        path: "/privacy",
        Component: PrivacyPolicyPage,
      },
      {
        path: "/terms",
        Component: TermsOfServicePage,
      },
      {
        path: "/cookies",
        Component: CookiePolicyPage,
      },
      {
        path: "/login",
        Component: LoginPage,
      },
      {
        path: "/signup",
        Component: SignupPage,
      },
      {
        path: "/reset-password",
        Component: ForgotPasswordPage,
      },
      {
        path: "/verify",
        Component: VerifyPage,
      },
      {
        path: "/staff-access",
        Component: StaffAccessPage,
      },
      {
        path: "/oauth",
        Component: OAuthPage,
      },
      {
        path: "/admin",
        Component: ProtectedLayout,
        errorElement: <GeneralError routeTo="/admin" />,
        children: [
          {
            index: true,
            element: <Navigate to="/admin/dashboard" replace />,
          },
          {
            path: "dashboard",
            lazy: async () => ({
              Component: (await import("../admin/dashboard/dashboard.page"))
                .default,
            }),
          },
          {
            path: "insights",
            lazy: async () => ({
              Component: (await import("../admin/insights/insights.page"))
                .default,
            }),
          },
          {
            path: "communications",
            lazy: async () => ({
              Component: (
                await import("../admin/communications/communications.page")
              ).default,
            }),
          },
          {
            path: "communications/:id",
            lazy: async () => ({
              Component: (
                await import(
                  "../admin/communications/communication-editor.page"
                )
              ).default,
            }),
          },
          {
            path: "communication/statistics/:communicationId",
            lazy: async () => ({
              Component: (
                await import(
                  "../admin/communications/communication-statistics.page"
                )
              ).default,
            }),
          },
          {
            path: "users",
            lazy: async () => ({
              Component: (await import("../admin/users/users.page")).default,
            }),
          },
          {
            path: "users/:id",
            lazy: async () => ({
              Component: (await import("../admin/users/user-profile.page"))
                .default,
            }),
          },
          {
            path: "qr-codes",
            lazy: async () => ({
              Component: (await import("../admin/qr-codes/qr-codes.page"))
                .default,
            }),
          },
          {
            path: "loyalty-program",
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (
                    await import(
                      "../admin/loyalty-program/loyalty-program.page"
                    )
                  ).default,
                }),
              },
              {
                path: "levels",
                lazy: async () => ({
                  Component: (
                    await import("../admin/loyalty-program/loyalty-levels.page")
                  ).default,
                }),
              },
            ],
          },
          {
            path: "loyalty-cards",
            children: [
              {
                index: true,
                element: (
                  <Navigate to="/admin/loyalty-cards/stamp-cards" replace />
                ),
              },
              {
                path: "stamp-cards",
                lazy: async () => ({
                  Component: (
                    await import("../admin/loyalty-cards/stamp-cards.page")
                  ).default,
                }),
              },
              {
                path: "stamp-cards/:id",
                lazy: async () => ({
                  Component: (
                    await import("../admin/loyalty-cards/stamp-card.page")
                  ).default,
                }),
              },
              {
                path: "gift-cards",
                lazy: async () => ({
                  Component: (await import("../coming-soon/coming-soon.page"))
                    .default,
                }),
              },
            ],
          },
          {
            path: "account",
            lazy: async () => ({
              Component: (await import("../admin/account/account.page"))
                .default,
            }),
          },
          {
            path: "settings",
            children: [
              {
                index: true,
                Component: SettingsPage,
              },
              {
                path: "administrators",
                lazy: async () => ({
                  Component: (
                    await import("../admin/settings/administrators.page")
                  ).default,
                }),
              },
            ],
          },
          {
            path: "subscription",
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (
                    await import("../admin/subscription/subscription.page")
                  ).default,
                }),
              },
              {
                path: "checkout",
                lazy: async () => ({
                  Component: (
                    await import("../admin/subscription/checkout.page")
                  ).default,
                }),
              },
              {
                path: "checkout/success",
                lazy: async () => ({
                  Component: (
                    await import("../admin/subscription/checkout-success.page")
                  ).default,
                }),
              },
            ],
          },
          {
            path: "paddle",
            children: [
              {
                path: "checkout",
                lazy: async () => ({
                  Component: (await import("../admin/paddle/checkout.page"))
                    .default,
                }),
              },
            ],
          },
        ],
      },
      {
        path: "/500",
        element: <GeneralError routeTo="/" />,
      },
      {
        path: "/404",
        element: <NotFoundError routeTo="/" />,
      },
      {
        path: "/503",
        element: <MaintenanceError routeTo="/" />,
      },
      {
        path: "/401",
        element: <UnauthorizedError routeTo="/" />,
      },
      {
        path: "*",
        element: <NotFoundError routeTo="/" />,
      },
    ],
  },
]);

export default router;
